import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ServiceLayout } from '@/components/ServiceLayout';
import {
  FileSpreadsheet, FileDown, RefreshCw, X, Search, Loader2,
  Trash2, Pencil, Check, ChevronDown, ChevronRight, BarChart3, Table as TableIcon,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { PageSizeNav } from '@/components/Service/PageSizeNav';
import { DateRangePresets, computePresetRange } from '@/components/Service/DateRangePresets';
import { BulkEditModal } from '@/components/Service/BulkEditModal';
import ReconciliationTab from '@/components/Service/ReconciliationTab';
import { MultiSelectFilter } from '@/components/Service/MultiSelectFilter';
import { useServiceMastersForFilters, intersectLiteral } from '@/hooks/useServiceMasters';
import { useServiceReportsFilters, REPORTS_STATUS_DEFAULTS } from '@/hooks/useServiceReportsFilters';
import { ReportTable } from '@/components/Service/ReportTable';
import { CompactMetricTile } from '@/components/Service/ReportMetricTiles';
import { useServiceConfig } from '@/hooks/useServiceConfig';
import { REPORT_KEY_MAP } from '@/hooks/serviceVisibilityDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCx = 'w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

// Tab configuration — defines all 8 reports in a single page
const TABS = {
  'vehicle-inwards':   { label: 'Vehicle Inwards',   dateField: 'vehicle_in_date',         groupBy: null,            shortLabel: 'Veh. In' },
  'vehicle-outwards':  { label: 'Vehicle Outwards',  dateField: 'expected_delivery_date',  groupBy: null,            shortLabel: 'Veh. Out' },
  'technician':        { label: 'Technician Wise',   dateField: 'vehicle_in_date',         groupBy: 'technician',    shortLabel: 'By Technician', groupByLabel: 'Technician' },
  'job-status':        { label: 'Job Status',        dateField: 'expected_delivery_date',  groupBy: 'doc_type',      shortLabel: 'By Status',     groupByLabel: 'Status' },
  'service-head':      { label: 'Service Head',      dateField: 'expected_delivery_date',  groupBy: 'service_head',  shortLabel: 'By Service Head', groupByLabel: 'Service Head' },
  'job-type':          { label: 'Job Type',          dateField: 'expected_delivery_date',  groupBy: 'job_type',      shortLabel: 'By Job Type', groupByLabel: 'Job Type' },
  'supervisor':        { label: 'Supervisor Wise',   dateField: 'expected_delivery_date',  groupBy: 'supervisor',    shortLabel: 'By Supervisor', groupByLabel: 'Supervisor' },
  'reconciliation':    { label: 'Reconciliation',    dateField: 'job_date',                groupBy: null,            shortLabel: 'Reconciliation', isReconciliation: true },
};

const TAB_ORDER = ['vehicle-inwards', 'vehicle-outwards', 'technician', 'job-status', 'service-head', 'job-type', 'supervisor', 'reconciliation'];

const COLORS = ['#0EA5E9', '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F43F5E'];

// Default preset for ALL reports — today → today
// Per-tab default date preset for the report landing view.
// Vehicle Inwards + Vehicle Outwards land on 'today'; the five operational
// reports (Technician Wise / Job Status / Service Head / Job Type /
// Supervisor Wise) land on fiscal-YTD so admins see the full year at a glance.
const YTD_DEFAULT_TABS = new Set(['technician', 'job-status', 'service-head', 'job-type', 'supervisor']);
const defaultPresetFor = (tab) => (YTD_DEFAULT_TABS.has(tab) ? 'ytd' : 'today');

export default function ServiceReports({ user, onLogout }) {
  const navigate = useNavigate();
  const { variant } = useParams();
  const activeTab = TABS[variant] ? variant : 'vehicle-inwards';
  const cfg = TABS[activeTab];

  // iter-120: Session-storage persistence for the non-hook-owned filter state
  // (date range, search, sort, viewTab, technician-tab filters). Preserves the
  // user's view across edit/view round-trips — the route unmounts when the
  // user clicks Edit/View on a row; saved filters restore on revisit.
  const REPORTS_STATE_KEY = 'service:reports:main-state';
  const REPORTS_STATE_VERSION = 1;
  const savedStateRef = React.useRef(null);
  if (savedStateRef.current === null) {
    try {
      const raw = sessionStorage.getItem(REPORTS_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      savedStateRef.current = (parsed && parsed.__v === REPORTS_STATE_VERSION && parsed.byTab) || {};
    } catch {
      savedStateRef.current = {};
    }
  }
  const savedForTab = savedStateRef.current[activeTab] || null;

  // View tab (Table or Charts)
  const [viewTab, setViewTab] = React.useState(savedForTab?.viewTab || 'table');

  // Date range
  const initialPreset = defaultPresetFor(activeTab);
  const [activePreset, setActivePreset] = React.useState(savedForTab?.activePreset ?? initialPreset);
  const [dateFrom, setDateFrom] = React.useState(() => savedForTab?.dateFrom || computePresetRange(initialPreset)[0]);
  const [dateTo, setDateTo] = React.useState(() => savedForTab?.dateTo || computePresetRange(initialPreset)[1]);

  // Filters
  const [filters, setFilters] = React.useState(savedForTab?.filters || {
    reg_no: '', party_name: '', mobile: '', supervisor: '', technician: '', job_type: '', service_head: '',
  });
  const [sortDir, setSortDir] = React.useState(
    savedForTab?.sortDir || (activeTab === 'vehicle-inwards' || activeTab === 'vehicle-outwards' ? 'desc' : 'asc')
  );

  // Unified Service Masters — filter-visible subset only. Data rows in each
  // report still render historical values directly from each record, so jobs
  // whose master is now hidden from filters still show correctly.
  const masters = useServiceMastersForFilters();

  // Field visibility configuration — per-report filters + date presets.
  // For `reconciliation` there's no entry (intentional — reconciliation is never gated).
  const { visibility } = useServiceConfig();
  const repVis = React.useMemo(() => {
    const key = REPORT_KEY_MAP[activeTab];
    return key ? visibility.reports[key] : null;
  }, [visibility, activeTab]);
  const repFilters = repVis?.filters || {};
  const repPresets = repVis?.date_presets;

  // Per-tab filter state — see useServiceReportsFilters.js for the full behaviour spec
  // (Vehicle Inwards bespoke defaults + per-tab session memory + Default-button branch).
  const {
    multiFilters, setMultiFilters,
    showPdi, setShowPdi,
    showDelivered, setShowDelivered,
    statusDefaultButtonValues,
    applyStatusDefault,
  } = useServiceReportsFilters({ activeTab, masters });

  // Technician-tab specific filters: secondary date range (ramp_in_date) + Job No search
  const [rampInFrom, setRampInFrom] = React.useState(savedForTab?.rampInFrom || '');
  const [rampInTo, setRampInTo] = React.useState(savedForTab?.rampInTo || '');
  const [jobNoQ, setJobNoQ] = React.useState(savedForTab?.jobNoQ || '');
  const statusOptions = masters.status || [];
  const statusSelected = multiFilters.doc_type;
  const setStatusSelected = (val) => {
    const next = typeof val === 'function' ? val(multiFilters.doc_type) : val;
    setMultiFilters((prev) => ({ ...prev, doc_type: next }));
  };

  // Data
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [metrics, setMetrics] = React.useState({ total: 0, by_service_head: {}, by_job_type: {}, by_technician: {}, by_supervisor: {} });

  // Selection
  const [selected, setSelected] = React.useState(new Set());
  const [bulkOpen, setBulkOpen] = React.useState(false);

  // Pagination (used only for non-grouped view)
  const [pageSize, setPageSize] = React.useState(savedForTab?.pageSize || 10);
  const [page, setPage] = React.useState(1);

  // iter-120: Persist ServiceReports-owned filter state to sessionStorage on
  // every change, scoped per active tab. Survives router-driven unmount
  // (e.g., user clicks Edit on a row) and restores on revisit.
  React.useEffect(() => {
    try {
      savedStateRef.current[activeTab] = {
        viewTab, activePreset, dateFrom, dateTo, filters, sortDir,
        rampInFrom, rampInTo, jobNoQ, pageSize,
      };
      sessionStorage.setItem(
        REPORTS_STATE_KEY,
        JSON.stringify({ __v: REPORTS_STATE_VERSION, byTab: savedStateRef.current })
      );
    } catch { /* noop */ }
  }, [activeTab, viewTab, activePreset, dateFrom, dateTo, filters, sortDir,
      rampInFrom, rampInTo, jobNoQ, pageSize]);

  // Group collapse state — DEFAULT: all groups collapsed (user explicitly requested)
  const [collapsedGroups, setCollapsedGroups] = React.useState(new Set());
  const [groupsInitialized, setGroupsInitialized] = React.useState(false);

  // Reset state when tab changes
  React.useEffect(() => {
    const np = defaultPresetFor(activeTab);
    setActivePreset(np);
    const [a, b] = computePresetRange(np);
    setDateFrom(a); setDateTo(b);
    setSortDir(activeTab === 'vehicle-inwards' || activeTab === 'vehicle-outwards' ? 'desc' : 'asc');
    setPage(1);
    setCollapsedGroups(new Set());
    setGroupsInitialized(false);
    setSelected(new Set());
    // Technician-only filters reset
    setRampInFrom('');
    setRampInTo('');
    setJobNoQ('');
  }, [activeTab]);

  React.useEffect(() => { setPage(1); }, [pageSize, dateFrom, dateTo, filters, sortDir]);

  const fetchReport = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('sort_dir', sortDir);
      params.set('date_field', cfg.dateField);
      const url = `${API}/service/reports/all?${params.toString()}`;
      const { data } = await axios.get(url);
      setRows(data.rows || []);
      setMetrics(data.metrics || { total: 0, by_service_head: {}, by_job_type: {}, by_technician: {}, by_supervisor: {} });
      setSelected(new Set());
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filters, sortDir, cfg.dateField]);

  React.useEffect(() => { fetchReport(); }, [fetchReport]);

  // Grouping with PDI filter applied
  const visibleRows = React.useMemo(() => {
    let base = rows;
    if (!showPdi) {
      base = base.filter((r) => {
        const sh = String(r.service_head || '').toLowerCase().trim();
        const jt = String(r.job_type || '').toLowerCase().trim();
        if (sh === 'pdi' || sh.includes('pdi')) return false;
        if (jt === 'pdi' || jt.includes('pdi')) return false;
        return true;
      });
    }

    // Multi-select filters from the new filter row (applied to all tabs)
    if (multiFilters.doc_type.length) {
      const set = new Set(multiFilters.doc_type.map((s) => s.toLowerCase()));
      base = base.filter((r) => set.has(String(r.doc_type || '').toLowerCase()));
    }
    if (multiFilters.service_head.length) {
      const set = new Set(multiFilters.service_head);
      base = base.filter((r) => set.has(r.service_head));
    }
    if (multiFilters.job_type.length) {
      const set = new Set(multiFilters.job_type);
      base = base.filter((r) => set.has(r.job_type));
    }
    if (multiFilters.supervisor.length) {
      const set = new Set(multiFilters.supervisor);
      base = base.filter((r) => set.has(r.supervisor));
    }
    if (multiFilters.technician.length) {
      const set = new Set(multiFilters.technician);
      base = base.filter((r) => set.has(r.technician));
    }
    if (!showDelivered) {
      base = base.filter((r) => !String(r.doc_type || '').toLowerCase().includes('deliver'));
    }

    // Technician-tab extra filters — applied client-side
    if (activeTab === 'technician') {
      // Secondary date filter: ramp_in_date range
      const parseDD = (s) => {
        if (!s) return null;
        const parts = String(s).split(/[-/]/);
        if (parts.length !== 3) return null;
        let d, m, y;
        if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; }
        else { d = parts[0]; m = parts[1]; y = parts[2]; if (y.length === 2) y = '20' + y; }
        const dt = new Date(`${y}-${m}-${d}T00:00:00`);
        return isNaN(dt.getTime()) ? null : dt;
      };
      const rDf = parseDD(rampInFrom);
      const rDt = parseDD(rampInTo);
      if (rDf || rDt) {
        base = base.filter((r) => {
          const rd = parseDD(r.ramp_in_date);
          if (!rd) return false;
          if (rDf && rd < rDf) return false;
          if (rDt && rd > rDt) return false;
          return true;
        });
      }
      // Job No search
      const jq = jobNoQ.trim().toLowerCase();
      if (jq) {
        base = base.filter((r) => String(r.job_no || '').toLowerCase().includes(jq));
      }
    }

    return base;
  }, [rows, showPdi, showDelivered, multiFilters, activeTab, rampInFrom, rampInTo, jobNoQ]);

  const grouped = React.useMemo(() => {
    if (!cfg.groupBy) return null;
    const map = new Map();
    visibleRows.forEach((r) => {
      const key = (r[cfg.groupBy] || 'Unassigned').toString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    // Sort group keys alphabetically (Unassigned last)
    return [...map.entries()]
      .sort((a, b) => {
        if (a[0] === 'Unassigned') return 1;
        if (b[0] === 'Unassigned') return -1;
        return a[0].localeCompare(b[0]);
      });
  }, [visibleRows, cfg.groupBy]);

  // Initialize all groups as COLLAPSED on first load (per user request)
  React.useEffect(() => {
    if (cfg.groupBy && grouped && grouped.length && !groupsInitialized) {
      setCollapsedGroups(new Set(grouped.map(([k]) => k)));
      setGroupsInitialized(true);
    }
  }, [grouped, cfg.groupBy, groupsInitialized]);

  const flatPaged = React.useMemo(() => {
    if (cfg.groupBy) return visibleRows; // grouped view shows all
    const start = (page - 1) * pageSize;
    return visibleRows.slice(start, start + pageSize);
  }, [visibleRows, page, pageSize, cfg.groupBy]);
  const flatTotalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));

  const clearAll = () => {
    setFilters({ reg_no: '', party_name: '', mobile: '', supervisor: '', technician: '', job_type: '', service_head: '' });
    const np = defaultPresetFor(activeTab);
    setActivePreset(np);
    const [a, b] = computePresetRange(np);
    setDateFrom(a); setDateTo(b);
  };

  const togglePick = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const exportRows = (fmtType) => {
    const target = selected.size ? rows.filter((r) => selected.has(r.id)) : rows;
    if (!target.length) { toast.error('No rows to export'); return; }

    // Build slim shape — same 15 columns user requested. Add Group column if grouped.
    const slim = target.map((r, i) => {
      const o = { S_No: i + 1 };
      if (cfg.groupBy) o[cfg.groupByLabel || cfg.groupBy] = r[cfg.groupBy] || 'Unassigned';
      o.Job_Status = r.doc_type || '';
      o.Supervisor = r.supervisor || '';
      o.Service_Head = r.service_head || '';
      o.Job_Category = r.job_category || '';
      o.Job_Type = r.job_type || '';
      o.Job_No = r.job_no || '';
      o.Job_Date = r.job_date || '';
      o.Customers_Voice = r.customer_voice || '';
      o.Chassis_No = r.chassis_no || '';
      o.Engine_No = r.engine_no || '';
      o.Model_Variant = r.model_variant || '';
      o.Reg_No = r.reg_no || '';
      o.Party_Name = r.party_name || '';
      o.Mobile_No = r.mobile_no_1 || '';
      o.Expected_Delivery = r.expected_delivery_date || '';
      return o;
    });

    return axios
      .post(`${API}/service/reports/export`, { rows: slim, format: fmtType, report_name: cfg.label }, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${cfg.label.replace(/ /g, '_')}.${fmtType === 'pdf' ? 'pdf' : 'xlsx'}`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success(`${fmtType.toUpperCase()} downloaded`);
      })
      .catch(() => toast.error('Export failed'));
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} selected job(s)?`)) return;
    try {
      const { data } = await axios.post(`${API}/service/jobs/bulk-delete`, { ids: [...selected] });
      toast.success(`${data.deleted} job(s) deleted`);
      fetchReport();
    } catch { toast.error('Bulk delete failed'); }
  };

  const handleBulkUpdate = async (updates) => {
    if (!Object.keys(updates).length) { toast.error('Set at least one field to update'); return; }
    try {
      const { data } = await axios.post(`${API}/service/jobs/bulk-update`, { ids: [...selected], updates });
      toast.success(`${data.modified} job(s) updated`);
      setBulkOpen(false);
      fetchReport();
    } catch { toast.error('Bulk update failed'); }
  };

  // Per-Job-Type breakdown — drives the metric tiles + the chartData when grouped by job_type.
  // Per-Job-Type breakdown — drives the metric tiles. Counts are computed from the UNFILTERED
  // (date-range only) `rows` so the tile lineup stays stable regardless of active multi-filters
  // (Option B per user request iter-103). Tiles with zero records are omitted to keep the row
  // tight on mobile; the user can widen the date range to bring missing job types back.
  const jobTypeBreakdown = React.useMemo(() => {
    const list = (masters.job_type || []);
    const norm = (s) => String(s || '').trim().toLowerCase();
    const counts = new Map(list.map((j) => [norm(j), 0]));
    rows.forEach((r) => {
      const k = norm(r.job_type);
      if (counts.has(k)) counts.set(k, counts.get(k) + 1);
    });
    // Slug each label and append index for any collisions so testIds remain unique
    // across master rows that normalise to the same slug ("Motor Repair/Replace" vs
    // "Motor Repair /Replace").
    const seen = {};
    return list
      .map((label) => {
        const base = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        const i = (seen[base] = (seen[base] || 0) + 1);
        const slug = i === 1 ? base : `${base}-${i}`;
        return { label, value: counts.get(norm(label)) || 0, slug };
      })
      .filter(({ value }) => value > 0);
  }, [rows, masters.job_type]);

  const jobTypeAccent = React.useCallback((label) => {
    const palette = ['indigo', 'violet', 'amber', 'emerald', 'rose', 'sky', 'fuchsia', 'teal'];
    let h = 0;
    for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) & 0xffff;
    return palette[h % palette.length];
  }, []);

  const handleJobTypeTileClick = React.useCallback((label) => {
    // Toggle: if the current filter is exactly [label], clear it; else set to [label].
    // Normalise via trim — keeps the comparison aligned with the table's filter pass.
    const normalized = String(label || '').trim();
    setMultiFilters((f) => {
      const current = f.job_type;
      const isSolo = current.length === 1 && String(current[0]).trim() === normalized;
      return { ...f, job_type: isSolo ? [] : [normalized] };
    });
  }, []);

  const handleEditJob = React.useCallback((jobId) => {
    if (!jobId) return;
    navigate(`/service/jobs/${jobId}/edit`);
  }, [navigate]);

  // Chart data — use the dimension that matches the active tab when possible
  const chartData = React.useMemo(() => {
    let bucket = metrics.by_service_head;
    if (cfg.groupBy === 'technician') bucket = metrics.by_technician;
    else if (cfg.groupBy === 'supervisor') bucket = metrics.by_supervisor;
    else if (cfg.groupBy === 'job_type') bucket = metrics.by_job_type;
    return Object.entries(bucket || {})
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [metrics, cfg.groupBy]);

  const headerActions = (
    <div className="flex items-center gap-2">
      <button onClick={fetchReport} data-testid="report-refresh" className="px-3.5 py-2 rounded-xl bg-white/70 border border-slate-200 text-slate-700 hover:bg-white text-[13px] font-medium flex items-center gap-2">
        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
      </button>
      <button onClick={() => exportRows('excel')} data-testid="export-excel" className="px-3.5 py-2 rounded-xl bg-white/70 border border-slate-200 text-slate-700 hover:bg-white text-[13px] font-medium flex items-center gap-2">
        <FileSpreadsheet size={14} /> Excel
      </button>
      <button onClick={() => exportRows('pdf')} data-testid="export-pdf" className="px-3.5 py-2 rounded-xl bg-white/70 border border-slate-200 text-slate-700 hover:bg-white text-[13px] font-medium flex items-center gap-2">
        <FileDown size={14} /> PDF
      </button>
    </div>
  );

  return (
    <ServiceLayout user={user} onLogout={onLogout} title={`${cfg.label} Report`} subtitle={cfg.isReconciliation ? 'Outstanding payments — SS × RT' : `${rows.length} records · ${dateFrom} → ${dateTo}`} actions={cfg.isReconciliation ? null : headerActions}>
      <div className="space-y-5" data-testid={`service-reports-${activeTab}`}>
        {/* Metrics tiles — Job Type breakdown, top of the page (below the report heading) */}
        {!cfg.isReconciliation && (
          <div
            className="grid gap-2 grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8"
            data-testid="job-type-metrics-grid"
          >
            <CompactMetricTile
              label="Total Jobs"
              value={metrics.total}
              accent="indigo"
              active={multiFilters.job_type.length === 0}
              onClick={() => setMultiFilters((f) => ({ ...f, job_type: [] }))}
              testId="metric-total-jobs"
            />
            {jobTypeBreakdown.map(({ label, value, slug }) => (
              <CompactMetricTile
                key={label}
                label={label}
                value={value}
                accent={jobTypeAccent(label)}
                active={multiFilters.job_type.length === 1 && String(multiFilters.job_type[0]).trim() === String(label).trim()}
                onClick={() => handleJobTypeTileClick(label)}
                testId={`metric-jobtype-${slug}`}
              />
            ))}
          </div>
        )}

        {/* Top Tab Bar — 8 reports */}
        <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-1.5 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {TAB_ORDER.map((id) => {
              const t = TABS[id];
              const isActive = id === activeTab;
              return (
                <button
                  key={id}
                  onClick={() => navigate(`/service/reports/${id}`)}
                  data-testid={`tab-${id}`}
                  className={`px-3.5 py-2 rounded-xl text-[12.5px] font-medium whitespace-nowrap transition-all
                    ${isActive
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-500/20'
                      : 'text-slate-600 hover:text-sky-700 hover:bg-sky-50'}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Reconciliation tab uses its own UI */}
        {cfg.isReconciliation ? (
          <ReconciliationTab user={user} />
        ) : (
        <>
        {/* Date range + Sort + Filters */}
        <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-4">
          <DateRangePresets
            activePreset={activePreset} setActivePreset={setActivePreset}
            dateFrom={dateFrom} setDateFrom={setDateFrom}
            dateTo={dateTo} setDateTo={setDateTo}
            sortDir={sortDir} setSortDir={setSortDir}
            visiblePresets={repPresets}
          />

          {/* NEW Multi-select filter row (master-driven) — above the search box */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            {[
              { k: 'doc_type',     label: 'Status',       mk: 'status',       vk: 'status' },
              { k: 'service_head', label: 'Service Head', mk: 'service_head', vk: 'service_head' },
              { k: 'job_type',     label: 'Job Type',     mk: 'job_type',     vk: 'job_type' },
              { k: 'supervisor',   label: 'Supervisor',   mk: 'supervisor',   vk: 'supervisor' },
              { k: 'technician',   label: 'Technician',   mk: 'technician',   vk: 'technician' },
            ].filter(({ vk }) => repFilters[vk] !== false).map(({ k, label, mk }) => (
              <MultiSelectFilter
                key={k}
                label={label}
                options={masters[mk] || []}
                selected={multiFilters[k]}
                onChange={(arr) => setMultiFilters((f) => ({ ...f, [k]: arr }))}
                testId={`report-multi-${k}`}
                defaults={k === 'doc_type' ? statusDefaultButtonValues : undefined}
                onApplyDefault={k === 'doc_type' ? applyStatusDefault : undefined}
              />
            ))}
            {repFilters.show_pdi !== false && (
              <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none px-2 py-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={showPdi}
                  onChange={(e) => setShowPdi(e.target.checked)}
                  data-testid="show-pdi-toggle-top"
                  className="w-4 h-4 rounded border-slate-300 text-sky-600"
                />
                Show PDI
              </label>
            )}
            {repFilters.show_delivered !== false && (
              <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none px-2 py-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={showDelivered}
                  onChange={(e) => setShowDelivered(e.target.checked)}
                  data-testid="show-delivered-toggle"
                  className="w-4 h-4 rounded border-slate-300 text-sky-600"
                />
                Show Delivered
              </label>
            )}
            {(multiFilters.doc_type.length + multiFilters.service_head.length + multiFilters.job_type.length + multiFilters.supervisor.length + multiFilters.technician.length) > 0 && (
              <button
                onClick={() => setMultiFilters({ doc_type: [], service_head: [], job_type: [], supervisor: [], technician: [] })}
                data-testid="report-multi-clear"
                className="text-[12px] text-sky-600 hover:underline px-2"
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5 mt-3">
            {[
              ['reg_no', 'Reg No'],
              ['party_name', 'Party Name'],
              ['mobile', 'Mobile No'],
              ['supervisor', 'Supervisor'],
              ['technician', 'Technician'],
              ['job_type', 'Job Type'],
              ['service_head', 'Service Head'],
            ].filter(([k]) => repFilters[k] !== false).map(([k, label]) => (
              <div key={k} className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={filters[k]}
                  onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value }))}
                  placeholder={label}
                  data-testid={`search-${k}`}
                  className={`${inputCx} pl-7`}
                />
                {filters[k] && (
                  <button onClick={() => setFilters((f) => ({ ...f, [k]: '' }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={clearAll}
              data-testid="clear-all"
              className="text-[12px] text-sky-600 hover:text-sky-800 px-2 py-2 inline-flex items-center justify-center gap-1 rounded-xl border border-dashed border-sky-200 hover:bg-sky-50"
            >
              <X size={12} /> Clear
            </button>
          </div>

          {/* Filter Tab — extra options below the search filters */}
          {cfg.groupBy && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-3">
              <span className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium">Filters</span>
              {repFilters.show_pdi !== false && (
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPdi}
                    onChange={(e) => setShowPdi(e.target.checked)}
                    data-testid="show-pdi-toggle"
                    className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-[12.5px] text-slate-700">Include PDI jobs</span>
                  <span className="text-[11px] text-slate-400">(hidden by default)</span>
                </label>
              )}
              {!showPdi && rows.length !== visibleRows.length && (
                <span className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                  {rows.length - visibleRows.length} row{rows.length - visibleRows.length === 1 ? '' : 's'} hidden
                </span>
              )}
              {grouped && grouped.length > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <button
                    onClick={() => setCollapsedGroups(new Set())}
                    data-testid="expand-all-groups"
                    className="text-[12px] text-sky-600 hover:text-sky-800 hover:underline"
                  >
                    Expand all
                  </button>
                  <button
                    onClick={() => setCollapsedGroups(new Set(grouped.map(([k]) => k)))}
                    data-testid="collapse-all-groups"
                    className="text-[12px] text-sky-600 hover:text-sky-800 hover:underline"
                  >
                    Collapse all
                  </button>
                </>
              )}
            </div>
          )}

          {/* Technician-tab specific: Ramp In secondary date range + Job No + Status multi-select */}
          {activeTab === 'technician' && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-1">Ramp In Date — From</label>
                  <input
                    type="text"
                    value={rampInFrom}
                    onChange={(e) => setRampInFrom(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    data-testid="tech-ramp-in-from"
                    className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-[12.5px] w-[140px]"
                  />
                </div>
                <div>
                  <label className="block text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-1">Ramp In Date — To</label>
                  <input
                    type="text"
                    value={rampInTo}
                    onChange={(e) => setRampInTo(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    data-testid="tech-ramp-in-to"
                    className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-[12.5px] w-[140px]"
                  />
                </div>
                {(rampInFrom || rampInTo) && (
                  <button onClick={() => { setRampInFrom(''); setRampInTo(''); }} className="text-[12px] text-sky-600 hover:underline">
                    Clear ramp dates
                  </button>
                )}
                <div className="relative">
                  <label className="block text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-1">Job No</label>
                  <Search size={12} className="absolute left-2.5 top-[31px] text-slate-400" />
                  <input
                    value={jobNoQ}
                    onChange={(e) => setJobNoQ(e.target.value)}
                    placeholder="Search Job No"
                    data-testid="tech-job-no-search"
                    className="pl-7 pr-3 py-1.5 rounded-lg bg-white border border-slate-200 text-[12.5px] w-[180px]"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium">Status:</span>
                <button
                  onClick={() => setStatusSelected([...statusOptions])}
                  data-testid="tech-status-select-all"
                  className="text-[11.5px] px-2 py-1 rounded-md text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100"
                >
                  Select All
                </button>
                <button
                  onClick={() => setStatusSelected(REPORTS_STATUS_DEFAULTS.filter((d) => statusOptions.includes(d)))}
                  data-testid="tech-status-defaults"
                  className="text-[11.5px] px-2 py-1 rounded-md text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100"
                >
                  Defaults
                </button>
                <button
                  onClick={() => setStatusSelected([])}
                  data-testid="tech-status-clear-all"
                  className="text-[11.5px] px-2 py-1 rounded-md text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100"
                >
                  Clear All
                </button>
                <span className="text-slate-300">·</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {statusOptions.length === 0 ? (
                    <span className="text-[11.5px] text-slate-400 italic">Loading status master…</span>
                  ) : statusOptions.map((s) => {
                    const isOn = statusSelected.includes(s);
                    return (
                      <button
                        key={s}
                        onClick={() => setStatusSelected((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])}
                        data-testid={`tech-status-${s}`}
                        className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-all
                          ${isOn ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300'}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View tabs: Table / Charts */}
        <div className="flex items-center gap-1 px-1.5 py-1.5 rounded-xl bg-white/70 border border-white/50 w-fit">
          <button
            onClick={() => setViewTab('table')}
            data-testid="view-tab-table"
            className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-all
              ${viewTab === 'table' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-600 hover:text-sky-700'}`}
          >
            <TableIcon size={13} /> A. Table
          </button>
          <button
            onClick={() => setViewTab('charts')}
            data-testid="view-tab-charts"
            className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-all
              ${viewTab === 'charts' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-600 hover:text-sky-700'}`}
          >
            <BarChart3 size={13} /> B. Charts
          </button>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-sky-50 border border-sky-200">
            <Check size={15} className="text-sky-600" />
            <span className="text-[13px] text-sky-900 font-medium">{selected.size} selected</span>
            <span className="flex-1" />
            <button onClick={() => setBulkOpen(true)} data-testid="report-bulk-edit" className="px-3 py-1.5 rounded-lg bg-white text-sky-700 border border-sky-200 text-[12.5px] font-medium hover:bg-sky-50 flex items-center gap-1.5">
              <Pencil size={13} /> Bulk Edit
            </button>
            <button onClick={handleBulkDelete} data-testid="report-bulk-delete" className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-[12.5px] font-medium hover:bg-rose-700 flex items-center gap-1.5">
              <Trash2 size={13} /> Bulk Delete
            </button>
          </div>
        )}

        {/* TABLE VIEW */}
        {viewTab === 'table' && (
          loading ? (
            <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 p-12 text-center">
              <Loader2 size={22} className="animate-spin text-sky-500 mx-auto" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 p-12 text-center text-slate-400 text-[13px]">
              No records for the selected filters.
            </div>
          ) : cfg.groupBy ? (
            // GROUPED VIEW (Multi Reports)
            <div className="space-y-3">
              {grouped.map(([groupKey, groupRows]) => {
                const isCollapsed = collapsedGroups.has(groupKey);
                return (
                  <div key={groupKey} className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
                    <button
                      onClick={() => toggleGroup(groupKey)}
                      data-testid={`group-${groupKey}`}
                      className="w-full flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white"
                    >
                      {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                      <span className="font-semibold text-[13.5px]">{cfg.groupByLabel || cfg.groupBy}: {groupKey}</span>
                      <span className="text-[12px] text-sky-100">({groupRows.length} {groupRows.length === 1 ? 'job' : 'jobs'})</span>
                    </button>
                    {!isCollapsed && (
                      <ReportTable rows={groupRows} selected={selected} togglePick={togglePick} onEdit={handleEditJob} showJobMetrics />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // FLAT VIEW (Vehicle Inwards / Outwards)
            <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <p className="text-[12.5px] text-slate-500">{rows.length} record{rows.length === 1 ? '' : 's'}</p>
                <PageSizeNav
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  page={page}
                  totalPages={flatTotalPages}
                  onPageChange={setPage}
                  testIdPrefix="report-pagesize"
                />
              </div>
              <ReportTable rows={flatPaged} selected={selected} togglePick={togglePick} onEdit={handleEditJob} startSno={(page - 1) * pageSize} showJobMetrics />
            </div>
          )
        )}

        {/* CHARTS VIEW */}
        {viewTab === 'charts' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-5">
              <h3 className="text-[14px] font-semibold text-slate-800 mb-1">
                Top {cfg.groupBy ? (cfg.groupByLabel || cfg.groupBy) + 's' : 'Service Heads'}
              </h3>
              <p className="text-[12px] text-slate-500 mb-3">Top 10 by job count</p>
              <div style={{ height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} interval={0} angle={-25} textAnchor="end" height={80} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#475569' }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="value" name="Jobs" radius={[6, 6, 0, 0]} fill="url(#skyGrad)" />
                    <defs>
                      <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0EA5E9" />
                        <stop offset="100%" stopColor="#6366F1" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-5">
              <h3 className="text-[14px] font-semibold text-slate-800 mb-1">Distribution</h3>
              <p className="text-[12px] text-slate-500 mb-3">Share by category</p>
              <div style={{ height: 360 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={120} paddingAngle={2}>
                      {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Bulk Edit Modal */}
      <BulkEditModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        count={selected.size}
        onApply={handleBulkUpdate}
      />
    </ServiceLayout>
  );
}
